import authService from './auth.service';

class ApiService {
  constructor() {
    this.baseUrls = {
      user: process.env.REACT_APP_USER_API_URL,
      scene: process.env.REACT_APP_SCENE_API_URL,
      renderer: process.env.REACT_APP_RENDERER_API_URL
    };
  }




  async request(service, endpoint, options = {}) {
    const baseUrl = this.baseUrls[service];
    const url = `${baseUrl}${endpoint}`;

    const headers = {
      'Accept': 'application/json',
      ...options.headers
    };

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (authService.isAuthenticated() &&
      !endpoint.includes('/login') &&
      !endpoint.includes('/request-password-reset') &&
      !endpoint.includes('/confirm-password-reset')) {
      const token = authService.getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const requestOptions = {
      ...options,
      headers,
      mode: 'cors'
    };

    const response = await fetch(url, requestOptions);

    // Handle token expiration
    if (response.status === 401 || response.status === 403) {
      // Token might be expired, try to refresh
      const refreshed = await authService.refreshToken();
      if (refreshed) {
        // Retry the request with new token
        const newToken = authService.getAccessToken();
        headers.Authorization = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, {
          ...options,
          headers,
          mode: 'cors'
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          console.error(`Retry failed for ${service} request to ${endpoint}:`, errorText);
          throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText} - ${errorText}`);
        }

        return retryResponse.json();
      } else {
        console.error('Token refresh failed, logging out user');
        // Refresh failed, redirect to login
        authService.logout();
        throw new Error('Authentication failed - token refresh unsuccessful');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Request failed for ${service} request to ${endpoint}:`, errorText);
      // OPUS-1148: surface the backend's SPECIFIC message (username/email already
      // exists, user/renderer limit reached, …) instead of a generic HTTP string.
      // Handles both shapes — user-mgmt { message } / { error, detail } and
      // renderer/scene { error: { message } }.
      let specific = '';
      try {
        const body = JSON.parse(errorText);
        specific = body?.error?.message
          || body?.message
          || (typeof body?.error === 'string' ? body.error : '')
          || body?.detail
          || '';
      } catch { /* not JSON — fall back below */ }
      if (response.status === 404 && !specific) {
        specific = `Not found — check the backend is running at ${baseUrl} and exposes ${endpoint}`;
      }
      const err = new Error(specific || `HTTP ${response.status}: ${response.statusText}`);
      err.status = response.status;
      err.body = errorText;
      throw err;
    }

    return response.json();
  }

  // ==================== USER APIs ====================

  // Authentication
  async getUserData(username) {
    return this.request('user', `/users/userdata/${username}`);
  }

  async login(credentials) {

    return this.request('user', '/users/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }

  // Cross-app handoff exchange — unauthenticated, the handoff JWT is the credential.
  async exchangeHandoff(handoff) {
    return this.request('user', '/handoff/exchange', {
      method: 'POST',
      body: JSON.stringify({ handoff })
    });
  }

  async refreshToken(refreshToken) {
    return this.request('user', '/users/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken })
    });
  }

  async requestPasswordReset(email) {
    return this.request('user', '/users/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async confirmPasswordReset(token, newPassword) {
    return this.request('user', '/users/confirm-password-reset', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
  }

  async getUsersByOrganization(orgId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('user', `/users/organization/${orgId}${queryString ? `?${queryString}` : ''}`);
  }

  async getUserById(userId) {
    return this.request('user', `/users/${userId}`);
  }

  async createUser(userData) {
    return this.request('user', '/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(userId, userData) {
    return this.request('user', `/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  // OPUS-899: update a user's entitlements (preview renderer + app access).
  async updateUserEntitlements(userName, entitlements) {
    return this.request('user', `/users/${userName}/entitlements`, {
      method: 'PUT',
      body: JSON.stringify(entitlements)
    });
  }

  async resendInvitation(username) {
    return this.request('user', `/users/${username}/resend-invitation`, {
      method: 'POST'
    });
  }

  async deactivateUser(username) {
    return this.request('user', `/users/${username}/inactive`, {
      method: 'PUT'
    });
  }

  async activateUser(username) {
    return this.request('user', `/users/${username}/enable`, {
      method: 'PUT'
    });
  }

  async markUserAsDeleted(username) {
    return this.request('user', `/users/${username}/inactive`, {
      method: 'PUT',
      body: JSON.stringify({ action: "deleted" })
    });
  }

  // Organizations
  async getOrganizations() {
    return this.request('user', '/common/organization');
  }

  async getOrganizationById(orgId) {
    return this.request('user', `/common/organization/${orgId}`);
  }

  async createOrganization(orgData) {
    return this.request('user', '/organization', {
      method: 'POST',
      body: JSON.stringify(orgData)
    });
  }

  async updateOrganization(orgId, orgData) {
    return this.request('user', `/common/organization/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(orgData)
    });
  }

  async updateOrganizationLogo(orgId, logoUrl) {
    return this.request('user', `/common/organization/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify({ logoUrl })
    });
  }

  async toggleOrganizationStatus(orgId, currentStatus) {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    return this.request('user', `/organization/${orgId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus })
    });
  }

  async deleteOrganization(orgId) {
    return this.request('user', `/common/organization/${orgId}`, { method: 'DELETE' });
  }

  // Roles
  async getRoles() {
    return this.request('user', '/common/roles');
  }

  // Permission catalog (OPUS-897) — canonical @ae/auth catalog, domain-grouped.
  async getPermissionCatalog() {
    return this.request('user', '/permissions/catalog');
  }

  async getRoleById(roleId) {
    return this.request('user', `/common/roles/${roleId}`);
  }

  async createRole(roleData) {
    return this.request('user', '/common/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    });
  }

  async updateRole(roleId, roleData) {
    return this.request('user', `/common/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData)
    });
  }

  async deleteRole(roleId) {
    return this.request('user', `/common/roles/${roleId}`, { method: 'DELETE' });
  }

  async getActivityLogs(orgId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('user', `/users/activities/${orgId}${queryString ? `?${queryString}` : ''}`);
  }

  async getUserActivityLogs(userId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('user', `/users/activities/${userId}/user${queryString ? `?${queryString}` : ''}`);
  }

  async createActivityLog(activityData) {
    return this.request('user', '/users/activities', {
      method: 'POST',
      body: JSON.stringify(activityData)
    });
  }


  async getSettings(orgId) {
    return this.request('user', `/settings/organization/${orgId}`);
  }

  // OPUS-897: super-admin view of every org's quick links.
  async getAllSettings() {
    return this.request('user', '/settings/all');
  }

  async updateSettings(orgId, settingsData) {
    return this.request('user', `/settings/organization/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
  }

  async addCTA(orgId, ctaData) {
    return this.request('user', `/settings/organization/${orgId}/cta`, {
      method: 'POST',
      body: JSON.stringify(ctaData)
    });
  }

  async updateCTA(orgId, ctaId, ctaData) {
    return this.request('user', `/settings/organization/${orgId}/cta/${ctaId}`, {
      method: 'PUT',
      body: JSON.stringify(ctaData)
    });
  }

  async deleteCTA(orgId, ctaId) {
    return this.request('user', `/settings/organization/${orgId}/cta/${ctaId}`, {
      method: 'DELETE'
    });
  }

  // OPUS-901/902: mint a short-lived signed handoff for opening another app.
  async mintHandoff(orgId) {
    return this.request('user', '/handoff/mint', {
      method: 'POST',
      body: JSON.stringify({ orgId }),
    });
  }

  // OPUS-907: cross-environment scene import (against THIS env's scene-service).
  async startSceneImport(body) {
    return this.request('scene', '/scene-import/start', { method: 'POST', body: JSON.stringify(body) });
  }
  async transferSceneImport(jobId, items) {
    return this.request('scene', `/scene-import/${jobId}/transfer`, { method: 'POST', body: JSON.stringify({ items }) });
  }
  async getSceneImportJob(jobId) {
    return this.request('scene', `/scene-import/${jobId}`);
  }
  async listSceneImportJobs() {
    return this.request('scene', '/scene-import');
  }


  async getOrganizationsFromScene() {
    return this.request('user', '/common/organization');
  }

  async setCaptionDisabled(sceneId, captionId, disabled) {
    return this.request('scene', `/scenes/${sceneId}/captions/${captionId}`, { method: 'PATCH', body: JSON.stringify({ disabled: !!disabled }) });
  }
  async getScenesByOrganization(orgId) {
    return this.request('scene', `/scenes/organization/${orgId}?includeOrg=true&includeUsers=true`);
  }

  async getSceneById(sceneId) {
    return this.request('scene', `/scenes/${sceneId}?includeUsers=true`);
  }

  async createScene(sceneData) {
    return this.request('scene', '/common/scene', {
      method: 'POST',
      body: JSON.stringify(sceneData)
    });
  }

  async getSceneFromUser(sceneId) {
    return this.request('user', `/common/scene/${sceneId}`);
  }

  /**
   * Update scene in User API. PUT /common/scene/:sceneId with full scene body
   */
  async updateScene(sceneId, sceneData) {
    return this.request('scene', `/common/scene/${sceneId}`, {
      method: 'PUT',
      body: JSON.stringify(sceneData)
    });
  }

  async saveCaptionPayload(orgId, sceneId, captionId, payload) {
    return this.request('scene', `common/scenes/${sceneId}/captions/${captionId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  async deleteScene(sceneId) {
    return this.request('scene', `/scenes/${sceneId}/delete`, { method: 'DELETE' });
  }

  async lockScene(sceneId) {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData?.data?._id;

    return this.request('scene', `/scenes/${sceneId}/lock`, {
      method: 'PATCH',
      body: JSON.stringify({ userId })
    });
  }

  async unlockScene(sceneId) {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData?.data?._id;

    return this.request('scene', `/scenes/${sceneId}/unlock`, {
      method: 'PATCH',
      body: JSON.stringify({ userId })
    });
  }

  // Files
  async uploadOrganizationLogo(file, orgId = null, orgName = "") {
    const formData = new FormData();
    formData.append('file', file);
    if (orgId) formData.append('organizationId', orgId);
    if (orgName) formData.append('organizationName', orgName);

    return this.request('scene', '/files/organization/logo', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData
    });
  }

  async uploadFile(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    Object.keys(options).forEach(key => {
      formData.append(key, options[key]);
    });

    return this.request('scene', '/files/upload', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData
    });
  }

  async getFiles(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('scene', `/files${queryString ? `?${queryString}` : ''}`);
  }

  async deleteFile(fileId) {
    return this.request('scene', `/files/${fileId}`, { method: 'DELETE' });
  }

  // S3
  async listS3Objects(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('scene', `/s3/list${queryString ? `?${queryString}` : ''}`);
  }

  // OPUS-1045: recursive folder size/count info point.
  async getS3FolderStats(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('scene', `/s3/stats${queryString ? `?${queryString}` : ''}`);
  }

  async createS3Folder(folderData) {
    return this.request('scene', '/s3/folder', {
      method: 'POST',
      body: JSON.stringify(folderData)
    });
  }

  async uploadToS3(uploadData) {
    const formData = new FormData();

    // Handle file upload
    if (uploadData.file) {
      formData.append('file', uploadData.file);
    }

    // Add other parameters
    if (uploadData.path) formData.append('path', uploadData.path);
    if (uploadData.organizationName) formData.append('organizationName', uploadData.organizationName);
    if (uploadData.sceneName) formData.append('sceneName', uploadData.sceneName);

    return this.request('scene', '/s3/upload', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData
    });
  }

  async deleteFromS3(key, options = {}) {
    return this.request('scene', '/s3/delete', {
      method: 'DELETE',
      body: JSON.stringify({ key, ...options })
    });
  }

  async getSignedUrl(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('scene', `/s3/signed-url${queryString ? `?${queryString}` : ''}`);
  }

  async copyS3Object(payload) {
    return this.request('scene', '/s3/copy', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async moveS3Object(payload) {
    return this.request('scene', '/s3/move', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async bulkDeleteS3(payload) {
    return this.request('scene', '/s3/bulk-delete', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async prepareZipDownload(payload) {
    return this.request('scene', '/s3/zip/prepare', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }


  // -------- Renderer Service --------

  async getRenderersByOrganization(orgId) {
    return this.request('renderer', `/v1/renderers/organization/${orgId}`);
  }

  async getRendererById(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}`);
  }

  // OPUS-1045: create a preview renderer for an existing program renderer.
  async createPreviewRenderer(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}/preview`, { method: 'POST', body: JSON.stringify({}) });
  }

  // OPUS-913: live presence (who is viewing a renderer now).
  async rendererHeartbeat(rendererId, avatar) {
    return this.request('renderer', `/v1/renderers/${rendererId}/presence/heartbeat`, { method: 'POST', body: JSON.stringify({ avatar: avatar || null }) });
  }
  async rendererLeave(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}/presence`, { method: 'DELETE' });
  }
  async getRendererPresence(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}/presence`);
  }
  async getRendererPresenceBatch(rendererIds) {
    return this.request('renderer', '/v1/renderers/presence/batch', { method: 'POST', body: JSON.stringify({ renderer_ids: rendererIds || [] }) });
  }

  // OPUS-913: region/IP access lock management (super-admin).
  async lockRenderer(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}/lock`, { method: 'POST' });
  }
  async unlockRenderer(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}/lock`, { method: 'DELETE' });
  }
  async getRendererAccessLock(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}/access-lock`);
  }
  async setRendererAccessLock(rendererId, lock) {
    return this.request('renderer', `/v1/renderers/${rendererId}/access-lock`, { method: 'PUT', body: JSON.stringify(lock) });
  }
  async getSceneAccessLock(sceneId) {
    return this.request('scene', `/scenes/${sceneId}/access-lock`);
  }
  async setSceneAccessLock(sceneId, lock) {
    return this.request('scene', `/scenes/${sceneId}/access-lock`, { method: 'PUT', body: JSON.stringify(lock) });
  }
  async getUserAccessLock(userName) {
    return this.request('user', `/users/${userName}/access-lock`);
  }
  async setUserAccessLock(userName, lock) {
    return this.request('user', `/users/${userName}/access-lock`, { method: 'PUT', body: JSON.stringify(lock) });
  }
  async getOrgAccessLock(orgId) {
    return this.request('user', `/organization/${orgId}/access-lock`);
  }
  async setOrgAccessLock(orgId, lock) {
    return this.request('user', `/organization/${orgId}/access-lock`, { method: 'PUT', body: JSON.stringify(lock) });
  }

  async createRenderer(rendererData) {
    return this.request('renderer', '/v1/renderers', {
      method: 'POST',
      body: JSON.stringify(rendererData)
    });
  }

  async updateRenderer(rendererId, rendererData) {

    return this.request('renderer', `/v1/renderers/${rendererId}`, {
      method: 'PATCH',
      body: JSON.stringify(rendererData)
    });
  }


  async deleteRenderer(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}`, {
      method: 'DELETE',
    });
  }


  // -------- Renderer Service --------

  // Renderer details (scene + captions etc) 
  async getRendererDetails(rendererId, options = {}) {
    const { attachOnlyScene = false, attachBothSceneCaption = false } = options;

    // Build query string
    const params = [];
    if (attachOnlyScene) params.push('attachOnlyScene=true');
    if (attachBothSceneCaption) params.push('attachBothSceneCaption=true');

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';

    return this.request('renderer', `/v1/renderers/${rendererId}/details${queryString}`);
  }

  async getRendererSceneCaption(rendererId) {
    return this.request('renderer', `/v1/renderers/${rendererId}/scenecaption`);
  }

  // Following endpoint still belong to Scene Service as Renderer Service does not have them

  async startRenderer(rendererId) {
    return this.request('scene', `/renderers/${rendererId}/start`, { method: 'POST' });
  }

  async stopRenderer(rendererId) {
    return this.request('scene', `/renderers/${rendererId}/stop`, { method: 'POST' });
  }

  async restartRenderer(rendererId) {
    return this.request('scene', `/renderers/${rendererId}/restart`, { method: 'POST' });
  }

  async getRendererStatus(rendererId) {
    return this.request('scene', `/renderers/${rendererId}/status`);
  }

  async getRendererLogs(rendererId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(
      'scene',
      `/renderers/${rendererId}/logs${queryString ? `?${queryString}` : ''}`
    );
  }


  // Commands
  async sendCommand(rendererId, command) {
    return this.request('scene', `/renderers/${rendererId}/command`, {
      method: 'POST',
      body: JSON.stringify(command)
    });
  }

  async getCommands(rendererId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request('scene', `/renderers/${rendererId}/commands${queryString ? `?${queryString}` : ''}`);
  }

  async updateRendererConfig(rendererId, config) {
    return this.request('scene', `/renderers/${rendererId}/config`, {
      method: 'PUT',
      body: JSON.stringify(config)
    });
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService; 